import React, { useContext, useEffect, useState } from 'react'
import * as S from './styles'

import { SurgicalOrderContext } from 'main/factories/pages/doctor/surgical-order/surgical-order-factory'
import { useServices } from 'presentation/hooks/use-services'

import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'

import { FinalizedOrderType } from 'domain/usecases/surgical-order/get-finalize-surgical-orders-history'
import AccordionCardNew from 'presentation/shared/components/AccordionCardNew'
import QueueNumber from '../PlanningTab/Accordions/QueueNumber'
import Procedures from '../PlanningTab/Accordions/Procedures'
import OpmeAndSuppliers from '../PlanningTab/Accordions/OpmeAndSuppliers'
import GeneralComments from '../PlanningTab/Accordions/GeneralComments'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import DocumentAccordeon from 'presentation/shared/layouts/DocumentAccordeon'
import moment from 'moment'

type Props = WithLoadingProps

const HistoryTab = ({ setIsLoading }: Props) => {
  const { state } = useContext<any>(SurgicalOrderContext)

  const [mainInformationAccordionOpen, setMainInformationAccordionOpen] =
    useState('')
  const [finalizedOrders, setFinalizedOrders] = useState(
    [] as FinalizedOrderType[]
  )

  const surgicalOrderService = useServices().surgicalOrder

  useEffect(() => {
    getFinalizedHistory()
  }, [])

  const getFinalizedHistory = async () => {
    try {
      setIsLoading(true)
      const response =
        await surgicalOrderService.getFinalizeSurgicalOrdersHistory({
          surgical_order_id: state.card.surgical_order_id
        })

      setFinalizedOrders(response.data)

      return response
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeMainInformationAccordion = (
    label: string,
    isExpanded: boolean
  ) => {
    setMainInformationAccordionOpen(!isExpanded ? '' : label)
  }

  const inactiveFinalizedOrders = finalizedOrders.filter((item) => {
    return item.is_active === false
  })

  const activeFinalizedOrders = finalizedOrders.filter((item) => {
    return item.is_active === true
  })

  return (
    <>
      <SupportTextNew
        size="medium"
        weight="normal"
        color={'neutral800'}
        style={{ marginBottom: '10px', marginTop: '20px' }}
      >
        Todas finalizações
      </SupportTextNew>

      <S.TabWrapper>
        <SupportTextNew weight="bold" color={'neutral800'}>
          Última atualização
        </SupportTextNew>

        {!!activeFinalizedOrders?.length && (
          <>
            <AccordionCardNew
              padding="10px"
              border="neutral100"
              margin="18px"
              header={
                <CustomHeader
                  date={activeFinalizedOrders[0].created_at}
                  isActive={activeFinalizedOrders[0].is_active}
                />
              }
              expanded={
                mainInformationAccordionOpen ===
                activeFinalizedOrders[0].finalize_surgical_order_id
              }
              accordionLabel={
                activeFinalizedOrders[0].finalize_surgical_order_id
              }
              handleChange={handleChangeMainInformationAccordion}
            >
              <AccordionContent finalizedItem={activeFinalizedOrders[0]} />
            </AccordionCardNew>
          </>
        )}

        <SupportTextNew
          weight="bold"
          color={'neutral800'}
          style={{ marginTop: '28px' }}
        >
          Versões anteriores
        </SupportTextNew>

        {!!inactiveFinalizedOrders?.length &&
          inactiveFinalizedOrders?.map((item) => {
            return (
              <>
                <AccordionCardNew
                  padding="10px"
                  border="neutral100"
                  margin="0px"
                  header={
                    <CustomHeader
                      date={item.created_at}
                      isActive={item.is_active}
                    />
                  }
                  expanded={
                    mainInformationAccordionOpen ===
                    item.finalize_surgical_order_id
                  }
                  accordionLabel={item.finalize_surgical_order_id}
                  handleChange={handleChangeMainInformationAccordion}
                >
                  <AccordionContent finalizedItem={item} />
                </AccordionCardNew>
              </>
            )
          })}
      </S.TabWrapper>
    </>
  )
}

type CustomHeaderProps = {
  date: string
  isActive: boolean
}

const CustomHeader = ({ date, isActive }: CustomHeaderProps) => {
  return (
    <>
      <SupportTextNew
        weight="bold"
        color={isActive ? 'primary600' : 'neutral800'}
      >
        Autorizado
      </SupportTextNew>

      <SupportTextNew weight="thin" color="neutral600" size="xsmall">
        {moment(date).utc().format('DD/MM/YYYY [às] HH[h]mm')}
      </SupportTextNew>
    </>
  )
}

export default WithLoading(HistoryTab)

type FinalizedOrderProps = {
  finalizedItem: FinalizedOrderType
}

const AccordionContent = ({ finalizedItem }: FinalizedOrderProps) => {
  const [finishedAccordionOpen, setFinishedAccordionOpen] = useState('')
  const [informationAccordionOpen, setInformationAccordionOpen] = useState('')

  const handleChangeFinishedAccordion = (
    label: string,
    isExpanded: boolean
  ) => {
    setFinishedAccordionOpen(!isExpanded ? '' : label)
  }

  const handleChangeInformationAccordion = (
    label: string,
    isExpanded: boolean
  ) => {
    setInformationAccordionOpen(!isExpanded ? '' : label)
  }

  return (
    <>
      <SupportTextNew weight="bold" color="black" style={{ marginTop: '18px' }}>
        Informações de finalização
      </SupportTextNew>

      <QueueNumber
        key="QueueNumber"
        expanded={finishedAccordionOpen === 'QueueNumber'}
        handleChange={handleChangeFinishedAccordion}
        password={finalizedItem?.password_to_finish}
        expirationText={finalizedItem?.password_max_date_validation_status}
        expirationDate={finalizedItem?.password_max_date_validation as any}
        passwordObservation={finalizedItem?.password_max_string_validation}
      />
      <Procedures
        isFinished
        key="ProceduresAndCid"
        expanded={informationAccordionOpen === 'Procedures'}
        handleChange={handleChangeInformationAccordion}
        procedures={finalizedItem.procedures as any}
        proceduresObservation={finalizedItem.procedure_observation}
      />
      {!!finalizedItem?.opme?.length && (
        <OpmeAndSuppliers
          isFinished
          key="OpmeAndSuppliers"
          expanded={informationAccordionOpen === 'OpmeAndSuppliers'}
          handleChange={handleChangeInformationAccordion}
          opmeSolicitations={finalizedItem?.opme}
          opmeFinalized={finalizedItem?.opme}
          opmeObservation={finalizedItem?.opme_observation}
          opmeDetails={finalizedItem?.opme_details}
        />
      )}
      <GeneralComments
        key="GeneralComments"
        expanded={finishedAccordionOpen === 'GeneralComments'}
        handleChange={handleChangeFinishedAccordion}
        comment={finalizedItem.general_observation}
      />

      {finalizedItem?.document?.document_url && (
        <>
          <SupportTextNew
            weight="bold"
            color="black"
            style={{ marginTop: '28px' }}
          >
            Documentos
          </SupportTextNew>

          <DocumentAccordeon
            gap="8px"
            document={finalizedItem?.document}
            fromUrl={!!finalizedItem?.document.document_url}
            shadow="satin"
            padding="18px"
            expandedDocument={informationAccordionOpen}
            handleChange={handleChangeInformationAccordion}
            showStatus={false}
          />
        </>
      )}
    </>
  )
}
